import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { PCB_CORE_URL } from "../../config";

import { useStore } from "../../store";

import "./quotedetail.css";



const API_BASE = (PCB_CORE_URL || "").replace(/\/+$/, "");

const API_QUOTE_PURCHASES = (id) => `${API_BASE}/superuser/quotes/${id}/purchases/`;

const STATUS_ALIASES = {

  launched: "submitted",

  dry_run: "preview",

  simulation: "preview",

};



function eur(n) {

  const x = Number(n || 0);

  return new Intl.NumberFormat("fr-FR", {

    style: "currency",

    currency: "EUR",

    minimumFractionDigits: 2,

    maximumFractionDigits: 2,

  }).format(x);

}



function normalizeStatus(s) {

  const normalized = String(s || "draft").toLowerCase();

  return STATUS_ALIASES[normalized] || normalized;

}



function normalizeSupplier(value) {

  return String(value || "").trim().toLowerCase();

}



const SUPPLIER_SHIPPING_RULES = {

  mouser: { threshold: 75, fee: 25 },

  rs: { threshold: 50, fee: 9.5 },

  farnell: { threshold: 50, fee: 11.99 },

  digikey: { threshold: 50, fee: 18 },

};



function supplierShippingAmount(supplier, componentsAmount) {

  const rule = SUPPLIER_SHIPPING_RULES[normalizeSupplier(supplier)];

  if (!rule) return 0;

  const amount = Number(componentsAmount || 0);

  return amount >= rule.threshold ? 0 : rule.fee;

}



function badgeClass(status) {

  const s = normalizeStatus(status);

  if (s === "completed") return "boBadge boBadgeOk";

  if (s === "submitted") return "boBadge boBadgePaid";

  if (s === "partial" || s === "preview") return "boBadge boBadgeInfo";

  return "boBadge";

}



function parseIntInput(v, fallback = 0) {

  const parsed = Number.parseInt(String(v ?? "").replace(/[^0-9-]/g, ""), 10);

  return Number.isFinite(parsed) ? parsed : fallback;

}



function hasMeaningfulValue(value) {

  return value !== null && value !== undefined && String(value).trim() !== "";

}



function humanizeKey(key) {

  return String(key || "")

    .replace(/_/g, " ")

    .replace(/([a-z])([A-Z])/g, "$1 $2")

    .replace(/\s+/g, " ")

    .trim()

    .replace(/\b\w/g, (m) => m.toUpperCase());

}



function flattenDisplayEntries(value, prefix = "") {

  if (Array.isArray(value)) {

    return value.flatMap((entry, index) => flattenDisplayEntries(entry, prefix ? `${prefix} ${index + 1}` : `${index + 1}`));

  }

  if (value && typeof value === "object") {

    return Object.entries(value).flatMap(([key, nested]) =>

      flattenDisplayEntries(nested, prefix ? `${prefix} - ${humanizeKey(key)}` : humanizeKey(key))

    );

  }

  if (!hasMeaningfulValue(value)) return [];

  return [[prefix || "Valeur", typeof value === "boolean" ? (value ? "Oui" : "Non") : String(value)]];

}



function pcbSpecEntries(spec) {

  if (!spec) return [];

  const baseEntries = [

    ["Mode PCB", spec.client_pcb_mode],

    ["Dimensions", spec.size_x && spec.size_y ? `${spec.size_x} x ${spec.size_y} mm` : ""],

    ["Couches", spec.layers],

    ["Épaisseur", spec.thickness ? `${spec.thickness} mm` : ""],

    ["Matériau", spec.material],

    ["Cuivre ext.", spec.external_copper_thickness],

    ["Cuivre int.", spec.inner_copper_thickness],

    ["Finition", spec.finish],

    ["Empilement", spec.stackup_type],

    ["Masque vernis dessus", spec.solder_color_top],

    ["Masque vernis dessous", spec.solder_color_bot],

    ["Sérigraphie", spec.silkscreen_ink_color],

    ["Contrôle impédance", spec.impedance_control === null || spec.impedance_control === undefined ? "" : spec.impedance_control ? "Oui" : "Non"],

  ].filter(([, value]) => hasMeaningfulValue(value));



  const seen = new Set(baseEntries.map(([label]) => String(label).toLowerCase()));

  const selectedChoices = spec && typeof spec.selected_choices === "object" && spec.selected_choices ? spec.selected_choices : {};

  const selectedEntries = Object.entries(selectedChoices).flatMap(([key, value]) =>

    flattenDisplayEntries(value, humanizeKey(key))

  );



  const selectedOffer = spec && typeof spec.selected_offer === "object" && spec.selected_offer ? spec.selected_offer : null;

  const selectedOfferEntries = selectedOffer

    ? flattenDisplayEntries(selectedOffer, "Offre sélectionnée")

    : [];



  for (const [label, value] of [...selectedEntries, ...selectedOfferEntries]) {

    const normalized = String(label).toLowerCase();

    if (!seen.has(normalized)) {

      baseEntries.push([label, value]);

      seen.add(normalized);

    }

  }



  const ignoredTopLevel = new Set([

    "id",

    "project",

    "created_at",

    "updated_at",

    "selected_choices",

    "selected_offer",

    "manual_quote_note",

    "manual_quote_note_updated_at",

  ]);

  Object.entries(spec).forEach(([key, value]) => {

    if (ignoredTopLevel.has(key)) return;

    if (value && typeof value === "object") return;

    const label = humanizeKey(key);

    const normalized = label.toLowerCase();

    if (!hasMeaningfulValue(value) || seen.has(normalized)) return;

    baseEntries.push([label, typeof value === "boolean" ? (value ? "Oui" : "Non") : String(value)]);

    seen.add(normalized);

  });



  if (baseEntries.length <= 2) {

    return Object.entries(spec).flatMap(([key, value]) => {

      if (key === "id" || key === "project") return [];

      if (value && typeof value === "object") {

        return flattenDisplayEntries(value, humanizeKey(key));

      }

      const rendered =

        value === null || value === undefined || String(value).trim() === ""

          ? "-"

          : typeof value === "boolean"

            ? value ? "Oui" : "Non"

            : String(value);

      return [[humanizeKey(key), rendered]];

    });

  }

  return baseEntries;

}



function assemblySpecEntries(spec) {

  if (!spec) return [];

  const rows = [

    ["PCB fourni client", spec.customer_supplies_pcb ? "Oui" : "Non"],

    ["Face CMS", spec.smd_side],

    ["Press-fit", spec.press_fit === null || spec.press_fit === undefined ? "" : spec.press_fit ? "Oui" : "Non"],

    ["Remplissage vias", spec.via_fill],

    ["Perçage min.", spec.min_drill_size ? `${spec.min_drill_size} mm` : ""],

  ].filter(([, value]) => hasMeaningfulValue(value));

  const seen = new Set(rows.map(([label]) => String(label).toLowerCase()));

  const ignored = new Set(["id", "project", "created"]);

  Object.entries(spec).forEach(([key, value]) => {

    if (ignored.has(key)) return;

    if (value && typeof value === "object") return;

    const label = humanizeKey(key);

    const normalized = label.toLowerCase();

    if (!hasMeaningfulValue(value) || seen.has(normalized)) return;

    rows.push([label, typeof value === "boolean" ? (value ? "Oui" : "Non") : String(value)]);

    seen.add(normalized);

  });

  if (rows.length <= 1) {

    return Object.entries(spec).flatMap(([key, value]) => {

      if (key === "id" || key === "project" || key === "created") return [];

      if (value && typeof value === "object") {

        return flattenDisplayEntries(value, humanizeKey(key));

      }

      const rendered =

        value === null || value === undefined || String(value).trim() === ""

          ? "-"

          : typeof value === "boolean"

            ? value ? "Oui" : "Non"

            : String(value);

      return [[humanizeKey(key), rendered]];

    });

  }

  return rows;

}



function fileRows(files) {

  if (!files || typeof files !== "object") return [];

  const labels = {

    gerber_zip: "Gerber ZIP",

    odb_archive: "Archive ODB++",

    pick_place_file: "Pick and place",

    assembly_plan_file: "Plan d'implantation",

    paste_top: "Paste top",

    paste_bot: "Paste bot",

    placement_file: "Fichier legacy placement",

    pick_place_in_archive: "Pick and place dans archive",

    assembly_plan_in_archive: "Plan d'implantation dans archive",

    paste_top_in_archive: "Paste top dans archive",

    paste_bot_in_archive: "Paste bot dans archive",

    notes: "Notes client",

  };

  return Object.entries(files)

    .map(([key, value]) => {

      const label = labels[key] || key;

      if (value && typeof value === "object") {

        return [label, value.present ? (value.name || "Oui") : "-"];

      }

      if (typeof value === "boolean") {

        return [label, value ? "Oui" : "Non"];

      }

      if (value === null || value === undefined || String(value).trim() === "") {

        return [label, "-"];

      }

      return [label, String(value)];

    });

}



function serializeItems(items) {

  return items.map((item) => ({

    key: item.key,

    qty_to_order: Math.max(parseIntInput(item.qty_to_order, item.planned_qty), 0),

    internal_reference: item.internal_reference || "",

    cost_center: item.cost_center || "",

  }));

}



function supplierButtonLabel(summary) {

  const status = normalizeStatus(summary?.status);

  if (summary?.already_ordered) return "Déjà commandé";

  if (summary?.simulation_only) return summary?.has_simulation ? "Relancer la simulation" : "Simuler";

  if (status === "failed") return "Relancer";

  if (status === "partial") return "Reprendre";

  return "Commander";

}



function supplierButtonStyle(summary) {

  const status = normalizeStatus(summary?.status);

  if (summary?.already_ordered) {

    return {

      borderColor: "rgba(80, 220, 140, 0.28)",

      background: "rgba(80, 220, 140, 0.14)",

    };

  }

  if (summary?.simulation_only || status === "preview") {

    return {

      borderColor: "rgba(255, 200, 80, 0.28)",

      background: "rgba(255, 200, 80, 0.14)",

    };

  }

  if (status === "failed") {

    return {

      borderColor: "rgba(255, 110, 110, 0.28)",

      background: "rgba(255, 110, 110, 0.14)",

    };

  }

  return undefined;

}



function latestSupplierResult(history, supplier) {

  const supplierKey = normalizeSupplier(supplier);

  for (const entry of Array.isArray(history) ? history : []) {

    const providerResults = Array.isArray(entry?.provider_results) ? entry.provider_results : [];

    const match = providerResults.find((result) => normalizeSupplier(result?.supplier) === supplierKey);

    if (match) return { entry, result: match };

  }

  return { entry: null, result: null };

}



function computeLineTotal(item) {

  const qty = Math.max(parseIntInput(item?.qty_to_order, 0), 0);

  const unitPrice = computePurchaseUnitPrice(item, qty);

  return unitPrice * qty;

}

function computePurchaseUnitPrice(item, qtyValue = null) {

  const qty = Math.max(parseIntInput(qtyValue ?? item?.qty_to_order, 0), 0);

  const basePrice = Number(item?.purchase_base_unit_price ?? item?.unit_price ?? item?.purchase_unit_price ?? 0);

  const breaks = Array.isArray(item?.purchase_price_breaks) ? item.purchase_price_breaks : [];

  return [...breaks].sort((a, b) => parseIntInput(a?.quantity, 0) - parseIntInput(b?.quantity, 0)).reduce((picked, row) => {

    const breakQty = Math.max(parseIntInput(row?.quantity, 0), 0);

    const price = Number(row?.unit_price ?? 0);

    if (breakQty > 0 && breakQty <= qty && price > 0) return price;

    return picked;

  }, basePrice);

}



export default function QuotePurchases() {

  const { quoteId } = useParams();

  const navigate = useNavigate();

  const access = useStore((s) => s.user.access);



  const apiFetch = useCallback(

    async (url, options = {}) => {

      return fetch(url, {

        ...options,

        headers: {

          "Content-Type": "application/json",

          ...(access ? { Authorization: `Bearer ${access}` } : {}),

          ...(options.headers || {}),

        },

      });

    },

    [access]

  );



  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [err, setErr] = useState("");

  const [ok, setOk] = useState("");

  const [payload, setPayload] = useState(null);

  const [items, setItems] = useState([]);

  const [dirtyKeys, setDirtyKeys] = useState(new Set());

  const [justSimulatedSupplier, setJustSimulatedSupplier] = useState(null);

  const [digikeyOAuth, setDigikeyOAuth] = useState(null); // null=loading, {has_refresh_token, access_token_valid}

  const [digikeyOAuthLoading, setDigikeyOAuthLoading] = useState(false);

  const dirtyTimers = useRef({});

  const digikeyOAuthConnected = Boolean(
    digikeyOAuth?.is_connected ?? (digikeyOAuth?.has_refresh_token && digikeyOAuth?.access_token_valid)
  );



  useEffect(() => {

    let alive = true;

    async function load() {

      setLoading(true);

      setErr("");

      setOk("");

      try {
        
        const res = await apiFetch(API_QUOTE_PURCHASES(quoteId), { method: "GET" });

        if (!res.ok) {

          const t = await res.text().catch(() => "");

          throw new Error(`HTTP ${res.status} ${t}`.trim());

        }

        const data = await res.json();

        if (!alive) return;

        setPayload(data);

        setItems(Array.isArray(data?.purchase?.items) ? data.purchase.items : []);

      } catch (e) {

        if (alive) setErr(e?.message || "Erreur de chargement des achats composants.");

      } finally {

        if (alive) setLoading(false);

      }

    }

    load();

    return () => {

      alive = false;

    };

  }, [apiFetch, quoteId]);



  useEffect(() => {

    apiFetch(`${API_BASE}/superuser/oauth/digikey/status/`, { method: "GET" })

      .then((r) => r.ok ? r.json() : null)

      .then((data) => { if (data) setDigikeyOAuth(data); })

      .catch(() => {});

  }, [apiFetch]);



  const refreshDigikeyOAuthStatus = useCallback(() => {

    apiFetch(`${API_BASE}/superuser/oauth/digikey/status/`, { method: "GET" })

      .then((r) => r.ok ? r.json() : null)

      .then((data) => { if (data) setDigikeyOAuth(data); })

      .catch(() => {});

  }, [apiFetch]);



  const connectDigikeyOAuth = useCallback(async () => {

    setDigikeyOAuthLoading(true);

    try {

      const res = await apiFetch(`${API_BASE}/superuser/oauth/digikey/authorize/`, { method: "GET" });

      const data = await res.json();

      if (data?.authorize_url) {

        const popup = window.open(data.authorize_url, "digikey_oauth", "width=600,height=700");

        const onMessage = (event) => {

          if (event.data === "digikey_oauth_success") {

            window.removeEventListener("message", onMessage);

            refreshDigikeyOAuthStatus();

            setOk("DigiKey OAuth connecté avec succès ! Vous pouvez maintenant passer des commandes DigiKey.");

          } else if (event.data === "digikey_oauth_error") {

            window.removeEventListener("message", onMessage);

            setErr("Échec de la connexion DigiKey OAuth. Réessayez.");

          }

        };

        window.addEventListener("message", onMessage);
        const timer = setInterval(() => {

          if (popup && popup.closed) {

            clearInterval(timer);

            window.removeEventListener("message", onMessage);

            refreshDigikeyOAuthStatus();

          }

        }, 1000);

      }

    } catch (e) {

      setErr("Erreur lors de la connexion DigiKey OAuth : " + (e?.message || ""));

    } finally {

      setDigikeyOAuthLoading(false);

    }

  }, [apiFetch, refreshDigikeyOAuthStatus]);



  const purchase = payload?.purchase || {};

  const quoteStatus = normalizeStatus(payload?.quote_status);

  const canLaunch = Boolean(payload?.can_launch);

  const rules = purchase.rules || {};

  const history = Array.isArray(purchase.history) ? purchase.history : [];

  const pcbRows = pcbSpecEntries(payload?.pcb_spec);

  const assemblyRows = assemblySpecEntries(payload?.assembly_spec);

  const files = fileRows(payload?.pcb_files);

  const rawPcbSpec = payload?.pcb_spec ? JSON.stringify(payload.pcb_spec, null, 2) : "";

  const rawAssemblySpec = payload?.assembly_spec ? JSON.stringify(payload.assembly_spec, null, 2) : "";



  const summary = useMemo(() => {

    const bySupplier = new Map();

    const nextSummary = (items || []).reduce(

      (acc, item) => {

        const requiredQty = Math.max(parseIntInput(item?.required_qty, 0), 0);

        const qtyToOrder = Math.max(parseIntInput(item?.qty_to_order, 0), 0);

        const lineTotal = computeLineTotal(item);

        const supplier = item?.supplier || "-";

        bySupplier.set(supplier, (bySupplier.get(supplier) || 0) + lineTotal);

        acc.lines_count += 1;

        acc.required_qty_total += requiredQty;

        acc.qty_to_order_total += qtyToOrder;

        acc.components_amount_total += lineTotal;

        return acc;

      },

      {

        lines_count: 0,

        required_qty_total: 0,

        qty_to_order_total: 0,

        components_amount_total: 0,

        shipping_amount_total: 0,

        amount_total: 0,

      }

    );

    nextSummary.shipping_amount_total = Array.from(bySupplier.entries()).reduce(

      (sum, [supplier, componentsAmount]) => sum + supplierShippingAmount(supplier, componentsAmount),

      0

    );

    nextSummary.components_total = nextSummary.components_amount_total;

    nextSummary.shipping_total = nextSummary.shipping_amount_total;

    nextSummary.amount_total = nextSummary.components_amount_total + nextSummary.shipping_amount_total;

    return nextSummary;

  }, [items]);



  const groupedItems = useMemo(() => {

    const map = new Map();

    for (const item of items) {

      const supplier = item?.supplier || "-";

      if (!map.has(supplier)) map.set(supplier, []);

      map.get(supplier).push(item);

    }

    return Array.from(map.entries()).map(([supplier, rows]) => {

      const apiSummary = (purchase.suppliers || []).find(

        (entry) => normalizeSupplier(entry?.supplier) === normalizeSupplier(supplier)

      ) || null;

      const componentsAmount = rows.reduce((sum, item) => sum + computeLineTotal(item), 0);

      const shippingAmount = supplierShippingAmount(supplier, componentsAmount);

      const supplierTotal = componentsAmount + shippingAmount;

      return {

        supplier,

        rows,

        summary: {

          ...apiSummary,

          supplier,

          lines_count: rows.length,

          required_qty: rows.reduce((sum, item) => sum + Math.max(parseIntInput(item?.required_qty, 0), 0), 0),

          qty_to_order: rows.reduce((sum, item) => sum + Math.max(parseIntInput(item?.qty_to_order, 0), 0), 0),

          components_amount: componentsAmount,

          components_total: componentsAmount,

          shipping_amount: shippingAmount,

          shipping_total: shippingAmount,

          amount: supplierTotal,

          total_amount: supplierTotal,

        },

      };

    });

  }, [items, purchase.suppliers]);



  function updateItem(key, field, value) {

    setItems((prev) =>

      prev.map((item) => {

        if (item.key !== key) return item;

        const next = { ...item, [field]: value };

        if (field === "qty_to_order") {

          const qty = Math.max(parseIntInput(value, item.qty_to_order), 0);

          const unitPrice = computePurchaseUnitPrice(item, qty);

          next.qty_to_order = qty;

          next.purchase_unit_price = unitPrice;

          next.unit_price = unitPrice;

          next.line_total = unitPrice * qty;

        }

        return next;

      })

    );

    if (field === "qty_to_order") {

      setDirtyKeys((prev) => new Set([...prev, key]));

      clearTimeout(dirtyTimers.current[key]);

      dirtyTimers.current[key] = setTimeout(() => {

        setDirtyKeys((prev) => {

          const next = new Set(prev);

          next.delete(key);

          return next;

        });

      }, 2000);

    }

  }



  async function saveDraft() {

    setSaving(true);

    setErr("");

    setOk("");

    try {

      

      const res = await apiFetch(API_QUOTE_PURCHASES(quoteId), {

        method: "PATCH",

        body: JSON.stringify({ items: serializeItems(items) }),

      });

      if (!res.ok) {

        const t = await res.text().catch(() => "");

        throw new Error(`HTTP ${res.status} ${t}`.trim());

      }

      const data = await res.json();

      setPayload(data);

      setItems(Array.isArray(data?.purchase?.items) ? data.purchase.items : []);

      setOk("Quantités d'achats enregistrées.");

    } catch (e) {

      setErr(e?.message || "Erreur d'enregistrement des achats composants.");

    } finally {

      setSaving(false);

    }

  }



  async function markCompleted() {

    setSaving(true);

    setErr("");

    setOk("");

    try {

      const res = await apiFetch(API_QUOTE_PURCHASES(quoteId), {

        method: "POST",

        body: JSON.stringify({ action: "complete" }),

      });

      if (!res.ok) {

        const t = await res.text().catch(() => "");

        throw new Error(`HTTP ${res.status} ${t}`.trim());

      }

      const data = await res.json();

      setPayload(data);

      setItems(Array.isArray(data?.purchase?.items) ? data.purchase.items : []);

      setOk("Achats composants marqués comme terminés.");

    } catch (e) {

      setErr(e?.message || "Erreur sur la clôture des achats composants.");

    } finally {

      setSaving(false);

    }

  }



  async function submitSupplier(summaryEntry) {

    if (!summaryEntry?.supplier) return;

    if (!canLaunch) {

      setErr("Le devis doit être payé avant de commander chez un fournisseur.");

      return;

    }



    const confirmationMessage =

      summaryEntry?.confirmation_message ||

      `Vous êtes sur le point de passer une commande réelle et payante auprès du fournisseur ${summaryEntry.supplier}. Confirmez-vous ?`;

    if (!window.confirm(confirmationMessage)) return;



    let confirmReorder = false;

    if (summaryEntry?.needs_reorder_warning) {

      const reorderMessage =

        summaryEntry?.reorder_warning_message ||

        "Une commande a déjà été effectuée. Voulez-vous en lancer une nouvelle ?";

      confirmReorder = window.confirm(reorderMessage);

      if (!confirmReorder) return;

    }



    setSaving(true);

    setErr("");

    setOk("");

    setJustSimulatedSupplier(null);

    try {

      const res = await apiFetch(API_QUOTE_PURCHASES(quoteId), {

        method: "POST",

        body: JSON.stringify({

          action: "submit_supplier",

          supplier: summaryEntry.supplier,

          items: serializeItems(items.filter((item) => item.supplier === summaryEntry.supplier)),

          confirm_real_order: true,

          confirm_reorder: confirmReorder,

        }),

      });

      if (!res.ok) {

        const t = await res.text().catch(() => "");

        throw new Error(`HTTP ${res.status} ${t}`.trim());

      }

      const data = await res.json();

      setPayload(data);

      setItems(Array.isArray(data?.purchase?.items) ? data.purchase.items : []);



      const latest = latestSupplierResult(data?.purchase?.history, summaryEntry.supplier);

      const resultStatus = normalizeStatus(latest?.result?.status || latest?.entry?.status || "");

      if (normalizeSupplier(summaryEntry.supplier) === "digikey") {
        refreshDigikeyOAuthStatus();
      }

      if (resultStatus === "preview") {

        setJustSimulatedSupplier(summaryEntry.supplier);
        setOk(`Simulation ${summaryEntry.supplier} réussie. Aucune commande réelle envoyée. Aucun paiement déclenché.`);

      } else if (resultStatus === "submitted") {

        setOk(`Commande ${summaryEntry.supplier} envoyée. Vérifiez l'historique pour le détail.`);

      } else if (resultStatus === "partial") {

        setOk(`Commande ${summaryEntry.supplier} partiellement traitée. Vérifiez l'historique pour les détails.`);

      } else {

        setOk(`Tentative ${summaryEntry.supplier} enregistrée. Consultez l'historique pour le résultat complet.`);

      }

    } catch (e) {

      setErr(e?.message || "Erreur lors de la commande fournisseur.");

    } finally {

      setSaving(false);

    }

  }



  if (loading) {

    return (

      <div className="boQD">

        <div className="boCard">

          <div className="boSkeletonRow" />

          <div className="boSkeletonRow" />

          <div className="boSkeletonRow" />

        </div>

      </div>

    );

  }



  if (err && !payload) {

    return (

      <div className="boQD">

        <div className="boCard boError">

          <div className="boErrorTitle">Erreur</div>

          <div className="boErrorText">{err}</div>

          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>

            <button className="boBtn" onClick={() => window.location.reload()}>

              Réessayer

            </button>

            <button className="boBtn boBtnGhost" onClick={() => navigate(-1)}>

              Retour

            </button>

          </div>

        </div>

      </div>

    );

  }



  return (

    <div className="boQD">

      <div className="boQDTop">

        <div>

          <div className="boQDTitleRow">

            <h1 className="boQDTitle">Achats composants : Devis #{payload?.quote_id}</h1>

            <span className={badgeClass(purchase.status)}>{purchase.status || "draft"}</span>

          </div>

          <div className="boQDSub">

            Commande interne : <span className="mono">{payload?.order_number || "-"}</span> - Statut devis :{" "}

            <span className="mono">{quoteStatus}</span>

          </div>

        </div>

        <div className="boQDTopActions">

          <button className="boBtn boBtnGhost" onClick={() => navigate(`/bo/quotes/${quoteId}`)}>

            Retour au devis

          </button>

        </div>

      </div>



      {err ? (

        <div className="boCard boError" style={{ marginBottom: 12 }}>

          <div className="boErrorTitle">Erreur</div>

          <div className="boErrorText">{err}</div>

        </div>

      ) : null}



      {ok ? (

        <div

          className="boCard"

          style={{ marginBottom: 12, borderColor: "rgba(34,197,94,0.28)", background: "rgba(34,197,94,0.08)" }}

        >

          <div className="boErrorTitle" style={{ color: "#bbf7d0" }}>

            Mise à jour

          </div>

          <div className="boErrorText">{ok}</div>

        </div>

      ) : null}



      <div className="boQDGrid">

        <div className="boCard">

          <div className="boSectionTitle">Récapitulatif</div>

          <div className="boKVRow">

            <div className="boKVKey">Références</div>

            <div className="boKVVal mono strong">{summary.lines_count || 0}</div>

          </div>

          <div className="boKVRow">

            <div className="boKVKey">Quantité nécessaire</div>

            <div className="boKVVal mono strong">{summary.required_qty_total || 0}</div>

          </div>

          <div className="boKVRow">

            <div className="boKVKey">Quantité à commander</div>

            <div className="boKVVal mono strong">{summary.qty_to_order_total || 0}</div>

          </div>

          <div className="boKVRow">

            <div className="boKVKey">Total composants</div>

            <div className="boKVVal mono strong">{eur(summary.components_amount_total ?? summary.components_total ?? summary.amount_total)}</div>

          </div>

          <div className="boKVRow">

            <div className="boKVKey">Frais de livraison</div>

            <div className="boKVVal mono strong">{eur(summary.shipping_amount_total ?? summary.shipping_total ?? 0)}</div>

          </div>

          <div className="boKVRow">

            <div className="boKVKey">Montant achats</div>

            <div className="boKVVal mono strong">{eur(summary.amount_total)}</div>

          </div>



          <div className="boDivider" />

          <div className="boSectionTitle" style={{ marginBottom: 8 }}>

            Règle appliquée

          </div>

          <div className="boHint" style={{ marginTop: 0 }}>

            Si le prix unitaire est inférieur au seuil, l'application calcule une quantité majorée, arrondie à l'entier, avec mini et maxi configurés.

          </div>

          <div className="boKVRow">

            <div className="boKVKey">Seuil prix unitaire</div>

            <div className="boKVVal mono">{eur(rules.price_threshold)}</div>

          </div>

          <div className="boKVRow">

            <div className="boKVKey">Majoration</div>

            <div className="boKVVal mono">{Number(rules.extra_pct || 0).toFixed(2).replace(/\.00$/, "")}%</div>

          </div>

          <div className="boKVRow">

            <div className="boKVKey">Mini</div>

            <div className="boKVVal mono">{rules.min_qty || 0}</div>

          </div>

          <div className="boKVRow">

            <div className="boKVKey">Maxi</div>

            <div className="boKVVal mono">{rules.max_qty || 0}</div>

          </div>



          <div className="boDivider" />

          <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>

            <button className="boBtn" onClick={saveDraft} disabled={saving}>

              Enregistrer le draft

            </button>

            <button className="boBtn" onClick={markCompleted} disabled={saving || items.length === 0}>

              Achats terminés

            </button>

          </div>

          {!canLaunch ? <div className="boHint">Les boutons fournisseurs s'activent uniquement quand le devis est payé.</div> : null}

        </div>



        <div className="boCard">

          <div className="boSectionTitle">Configuration PCB du client</div>

          {pcbRows.length || assemblyRows.length ? (

            <>

              <div className="boFormGrid">

                {pcbRows.map(([label, value], index) => (

                  <div key={`pcb-${label}-${index}`} className="boKVRow" style={{ padding: 0, borderBottom: "none" }}>

                    <div className="boKVKey">{label}</div>

                    <div className="boKVVal mono">{String(value)}</div>

                  </div>

                ))}

                {assemblyRows.map(([label, value], index) => (

                  <div key={`asm-${label}-${index}`} className="boKVRow" style={{ padding: 0, borderBottom: "none" }}>

                    <div className="boKVKey">{label}</div>

                    <div className="boKVVal mono">{String(value)}</div>

                  </div>

                ))}

              </div>

              {/* <details style={{ marginTop: 12 }}>

                <summary className="boHint" style={{ cursor: "pointer" }}>Voir les données brutes PCB / assemblage</summary>

                {rawPcbSpec ? <pre className="mono" style={{ marginTop: 10, whiteSpace: "pre-wrap", fontSize: 12, opacity: 0.9 }}>{rawPcbSpec}</pre> : null}

                {rawAssemblySpec ? <pre className="mono" style={{ marginTop: 10, whiteSpace: "pre-wrap", fontSize: 12, opacity: 0.9 }}>{rawAssemblySpec}</pre> : null}

              </details> */}

            </>

          ) : (

            <div className="boHint" style={{ marginTop: 0 }}>

              Aucune configuration PCB enregistrée.

            </div>

          )}

        </div>



        <div className="boCard">

          <div className="boSectionTitle">Fichiers fournis par le client</div>

          {files.length ? (

            <div className="boFormGrid">

              {files.map(([label, value]) => (

                <div key={label} className="boKVRow" style={{ padding: 0, borderBottom: "none" }}>

                  <div className="boKVKey">{label}</div>

                  <div className="boKVVal mono">{value}</div>

                </div>

              ))}

            </div>

          ) : (

            <div className="boHint" style={{ marginTop: 0 }}>

              Aucun fichier PCB client enregistré.

            </div>

          )}

        </div>

      </div>



      {/* Steps done / remaining */}
      {(purchase.steps_done?.length > 0 || purchase.steps_remaining?.length > 0) && (

        <div className="boCard" style={{ marginTop: 16 }}>

          <div className="boSectionTitle">Avancement des achats</div>

          {purchase.steps_done?.length > 0 && (

            <div style={{ marginBottom: 8 }}>

              <div className="boHint" style={{ marginBottom: 4, color: "#86efac" }}>Effectués :</div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>

                {purchase.steps_done.map((s) => (

                  <span key={s.supplier} className="boBadge boBadgeOk">{s.supplier} ({s.status})</span>

                ))}

              </div>

            </div>

          )}

          {purchase.steps_remaining?.length > 0 && (

            <div>

              <div className="boHint" style={{ marginBottom: 4, color: "#fbbf24" }}>Restants :</div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>

                {purchase.steps_remaining.map((s) => (

                  <span key={s.supplier} className="boBadge">{s.supplier}</span>

                ))}

              </div>

            </div>

          )}

        </div>

      )}



      {groupedItems.map((group) => (

        <div key={group.supplier} className="boCard" style={{ marginTop: 16 }}>

          <div className="boSectionTitle" style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start" }}>

            <div style={{ minWidth: 0 }}>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>

                <span>{group.supplier}</span>

                <span className={badgeClass(group.summary?.status || "draft")}>{group.summary?.status || "draft"}</span>

                {group.summary?.simulation_only ? <span className="boBadge boBadgeInfo">Simulation - aucune commande réelle</span> : null}

              </div>

              <div className="boHint" style={{ marginTop: 6 }}>

                {group.summary

                  ? `${group.summary.lines_count} refs / ${group.summary.qty_to_order} pcs / ${eur(group.summary.amount)}`

                  : `${group.rows.length} refs`}

              </div>

              {group.summary?.last_batch_number ? (

                <div className="boHint">

                  Lot #{group.summary.last_batch_number} - {group.summary.last_ordered_at || "-"} {group.summary.last_ordered_by ? `- ${group.summary.last_ordered_by}` : ""}

                </div>

              ) : null}

              {group.summary?.last_result?.message ? <div className="boHint">{group.summary.last_result.message}</div> : null}

              {group.summary?.needs_reorder_warning ? (

                <div className="boHint" style={{ color: "#fbbf24" }}>

                  {group.summary.reorder_warning_message}

                </div>

              ) : null}

              {group.summary?.already_ordered ? (

                <div className="boHint" style={{ color: "#86efac" }}>

                  Ce fournisseur a déjà reçu une commande. Le bouton reste actif pour permettre une recommande.

                </div>

              ) : null}

            </div>



            <div style={{ display: "grid", gap: 8, justifyItems: "end", minWidth: 220 }}>

              {/* Bouton OAuth DigiKey si non connecté */}
              {normalizeSupplier(group.supplier) === "digikey" && digikeyOAuth && !digikeyOAuthConnected && (

                <button

                  className="boBtn boBtnPrimary"

                  style={{ background: "#f59e0b", borderColor: "#f59e0b" }}

                  onClick={connectDigikeyOAuth}

                  disabled={digikeyOAuthLoading}

                >

                  {digikeyOAuthLoading ? "Connexion..." : (digikeyOAuth.has_refresh_token ? "Reconnecter DigiKey OAuth" : "Connecter DigiKey OAuth")}

                </button>

              )}

              {normalizeSupplier(group.supplier) === "digikey" && digikeyOAuthConnected && (

                <span className="boBadge boBadgeOk" style={{ alignSelf: "center" }}>

                  DigiKey OAuth connecté

                </span>

              )}

              {normalizeSupplier(group.supplier) === "digikey" && digikeyOAuth?.refresh_error && !digikeyOAuthConnected && (

                <span className="boHint" style={{ color: "#fbbf24", textAlign: "right", maxWidth: 260 }}>

                  Connexion DigiKey expirée ou refusée. Reconnectez OAuth.

                </span>

              )}

              <button

                className={["boBtn", group.summary?.already_ordered || group.summary?.simulation_only ? "" : "boBtnPrimary"].filter(Boolean).join(" ")}

                style={supplierButtonStyle(group.summary)}

                onClick={() => submitSupplier(group.summary)}

                disabled={saving || !canLaunch}

              >

                {justSimulatedSupplier === group.supplier ? "Simulation envoyée" : supplierButtonLabel(group.summary)}

              </button>

              {!canLaunch ? (

                <span className="boHint">Devis non payé</span>

              ) : group.summary?.simulation_only ? (

                <span className="boHint">Simulation uniquement - aucun envoi réel, aucun paiement déclenché.</span>

              ) : (

                <span className="boHint">Commande réelle via API fournisseur après confirmation.</span>

              )}

              {group.summary?.simulation_only ? (

                <span className="boHint" style={{ color: "#fbbf24", fontStyle: "italic" }}>

                  Ceci est une simulation. Aucun achat n'a été effectué chez {group.supplier}.

                </span>

              ) : null}

            </div>

          </div>

          <div className="boTableWrap">

            <table className="boTable">

              <thead>

                <tr>

                  <th>RÉFS</th>

                  <th>MPN</th>

                  <th>SKU fournisseur</th>

                  <th className="right">Qté nécessaire</th>

                  <th className="right">Qté prévue</th>

                  <th className="right">Qté commandée</th>

                  <th className="right">PU achat</th>
                  <th className="right">PU facturé</th>

                  <th className="right">Montant</th>

                  <th>Réf. interne</th>

                </tr>

              </thead>

              <tbody>

                {group.rows.map((item) => {

                  const isDirty = dirtyKeys.has(item.key);

                  return (

                  <tr key={item.key} className={isDirty ? "boTableRowDirty" : ""}>

                    <td className="mono">{item.refs || "-"}</td>

                    <td className="mono">{item.mpn || "-"}</td>

                    <td className="mono">{item.supplier_sku || "-"}</td>

                    <td className="right mono">{item.required_qty}</td>

                    <td className="right mono">{item.planned_qty}</td>

                    <td className="right" style={{ minWidth: 110 }}>

                      <input

                        className="boInput mono"

                        value={item.qty_to_order}

                        onChange={(e) => updateItem(item.key, "qty_to_order", e.target.value)}

                        inputMode="numeric"

                      />

                    </td>

                    <td className="right mono">{eur(item.purchase_unit_price ?? item.unit_price)}</td>
                    <td className="right mono">{eur(item.client_unit_price ?? item.unit_price)}</td>

                    <td className={["right mono strong", isDirty ? "boAmountDirty" : ""].filter(Boolean).join(" ")}>{eur(computeLineTotal(item))}</td>

                    <td style={{ minWidth: 180 }}>

                      <input

                        className="boInput mono"

                        value={item.internal_reference || ""}

                        onChange={(e) => updateItem(item.key, "internal_reference", e.target.value)}

                      />

                    </td>

                  </tr>

                  );

                })}

              </tbody>

              <tfoot>

                <tr>

                  <td colSpan="8" className="right mono strong">

                    Total composants

                  </td>

                  <td className={["right mono strong", group.rows.some((item) => dirtyKeys.has(item.key)) ? "boAmountDirty" : ""].filter(Boolean).join(" ")}>{eur(group.summary?.components_amount ?? group.summary?.components_total ?? 0)}</td>

                  <td />

                </tr>

                <tr>

                  <td colSpan="8" className="right mono strong">

                    Frais de livraison

                  </td>

                  <td className={["right mono strong", group.rows.some((item) => dirtyKeys.has(item.key)) ? "boAmountDirty" : ""].filter(Boolean).join(" ")}>{eur(group.summary?.shipping_amount ?? group.summary?.shipping_total ?? 0)}</td>

                  <td />

                </tr>

                <tr>

                  <td colSpan="8" className="right mono strong">

                    Total fournisseur

                  </td>

                  <td className={["right mono strong", group.rows.some((item) => dirtyKeys.has(item.key)) ? "boAmountDirty" : ""].filter(Boolean).join(" ")}>{eur(group.summary?.amount ?? group.summary?.total_amount ?? 0)}</td>

                  <td />

                </tr>

              </tfoot>

            </table>

          </div>

        </div>

      ))}



      <div className="boCard" style={{ marginTop: 16 }}>

        <div className="boSectionTitle">Historique</div>

        {history.length ? (

          history.map((entry) => (

            <div key={entry.batch_number} style={{ padding: "12px 0", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>

              <div className="boKVRow" style={{ alignItems: "flex-start", padding: 0, borderBottom: "none" }}>

                <div>

                  <div className="strong">Lot #{entry.batch_number}</div>

                  <div className="boHint" style={{ marginTop: 4 }}>

                    Créé le {entry.created_at || "-"} par {entry.actor_email || "-"}

                  </div>

                </div>

                <div className="boKVVal" style={{ textAlign: "right" }}>

                  <div className="mono strong">{entry.status || "-"}</div>

                  <div className="boHint" style={{ marginTop: 4 }}>{eur(entry?.summary?.amount_total)}</div>

                </div>

              </div>

            </div>

          ))

        ) : (

          <div className="boHint" style={{ marginTop: 0 }}>

            Aucun lancement enregistré.

          </div>

        )}

      </div>

    </div>

  );

}

